import { normalizeFrameTo16k } from "@/audio/resampler";
import { decodeWav, duplicateMonoToStereo, mixToMono } from "@/audio/wav";
import { APP_CONFIG } from "@/config/app-config";
import { getOpfsPathForManifestFile } from "@/install/install-flow";
import type { RuntimeBus } from "@/runtime/bus";
import { getOnnxRuntime } from "@/runtime/onnx-runtime";
import { getQwenSmokeProbeLabel, runQwenPromptCheck } from "@/runtime/qwen-runtime";
import type { InstallRecord, Lane, LaneManifest, SmokeCheckRecord } from "@/runtime/events";
import { readOpfsBytes } from "@/storage/opfs";
import { checkEnergyVadOnSilence, detectSpeechWithEnergy } from "@/runtime/vad-energy";
import { smokeWhisperTranscribe } from "@/runtime/whisper-stt-runtime";

interface FixtureAudio {
  mono48: Float32Array;
  mono16: Float32Array;
  sampleRate: number;
}

let fixturePromise: Promise<FixtureAudio> | null = null;

function makeSmokeRecord(
  probe: string,
  status: SmokeCheckRecord["status"],
  summary: string,
  metrics?: SmokeCheckRecord["metrics"],
): SmokeCheckRecord {
  return {
    status,
    checkedAt: status === "pending" ? null : new Date().toISOString(),
    probe,
    summary,
    metrics,
  };
}

export function getLaneSmokeProbeLabel(lane: Lane): string {
  return lane === "llm" ? getQwenSmokeProbeLabel() : APP_CONFIG.fixtureAudioLabel;
}

function findManifestFilePath(manifest: LaneManifest, suffix: string): string {
  const match = manifest.files.find((file) => file.path.endsWith(suffix));

  if (!match) {
    throw new Error(`Missing ${suffix} in ${manifest.id}`);
  }

  return getOpfsPathForManifestFile(manifest, match.path);
}

async function loadFixtureAudio(): Promise<FixtureAudio> {
  if (!fixturePromise) {
    fixturePromise = (async () => {
      const response = await fetch(APP_CONFIG.fixtureAudioPath, { cache: "force-cache" });

      if (!response.ok) {
        throw new Error(`Failed to fetch fixture ${APP_CONFIG.fixtureAudioLabel}`);
      }

      const decoded = decodeWav(await response.arrayBuffer());
      const mono48 = mixToMono(decoded);

      return {
        mono48,
        mono16: normalizeFrameTo16k(mono48, decoded.sampleRate),
        sampleRate: decoded.sampleRate,
      };
    })();
  }

  return fixturePromise;
}

function floatTensorData(value: unknown): Float32Array {
  if (
    value &&
    typeof value === "object" &&
    "data" in value &&
    value.data instanceof Float32Array
  ) {
    return value.data;
  }

  throw new Error("Expected Float32 tensor output");
}

function intTensorData(value: unknown): Int32Array {
  if (
    value &&
    typeof value === "object" &&
    "data" in value &&
    value.data instanceof Int32Array
  ) {
    return value.data;
  }

  throw new Error("Expected Int32 tensor output");
}

function tensorDims(value: unknown): readonly number[] {
  if (
    value &&
    typeof value === "object" &&
    "dims" in value &&
    Array.isArray(value.dims)
  ) {
    return value.dims as number[];
  }

  throw new Error("Expected tensor dims");
}

function meanAbsolute(values: Float32Array): number {
  if (!values.length) {
    return 0;
  }

  let total = 0;

  for (let index = 0; index < values.length; index += 1) {
    total += Math.abs(values[index]);
  }

  return total / values.length;
}

function createFixtureWarmupFeatures(audio16: Float32Array): Float32Array {
  const frameCount = 3000;
  const featureBins = 80;
  const hop = 160;
  const window = 400;
  const envelope = new Float32Array(frameCount);

  for (let frame = 0; frame < frameCount; frame += 1) {
    const start = frame * hop;
    const end = Math.min(audio16.length, start + window);
    let sumSquares = 0;
    let sampleCount = 0;

    for (let index = start; index < end; index += 1) {
      const sample = audio16[index] ?? 0;
      sumSquares += sample * sample;
      sampleCount += 1;
    }

    const rms = sampleCount ? Math.sqrt(sumSquares / sampleCount) : 0;
    envelope[frame] = Math.log10(1 + rms * 64);
  }

  const features = new Float32Array(featureBins * frameCount);

  for (let bin = 0; bin < featureBins; bin += 1) {
    const radius = 1 + Math.floor(bin / 10);
    const gain = 1 + bin / 96;

    for (let frame = 0; frame < frameCount; frame += 1) {
      let smoothed = 0;
      let taps = 0;

      for (let offset = -radius; offset <= radius; offset += 1) {
        const index = frame + offset;
        if (index < 0 || index >= frameCount) {
          continue;
        }

        smoothed += envelope[index];
        taps += 1;
      }

      features[bin * frameCount + frame] = (smoothed / Math.max(1, taps)) * gain;
    }
  }

  return features;
}

async function releaseSession(session: unknown): Promise<void> {
  if (
    session &&
    typeof session === "object" &&
    "release" in session &&
    typeof session.release === "function"
  ) {
    await session.release();
  }
}

async function runVadSmokeCheck(_manifest: LaneManifest): Promise<SmokeCheckRecord> {
  // Energy-based VAD smoke. Does NOT load any ONNX model and does NOT call
  // `ort.InferenceSession.create()`. The Silero+ORT VAD path triggered iOS
  // jetsam on iPhone Safari ~30 s after the smoke ran reproducibly (see
  // `docs/09-lane-isolation-plan.md` § VAD and bench 05/06/07 evidence).
  //
  // The replacement runs the same algorithm as
  // `bench/experiments/07-vad-worklet-energy/vad-worklet.js` but in batch
  // form via `detectSpeechWithEnergy`, which is the same function the
  // worker uses for live-turn VAD now.
  const fixture = await loadFixtureAudio();
  const speech = await detectSpeechWithEnergy(fixture.mono16);
  const silenceDiagnostics = checkEnergyVadOnSilence();

  return makeSmokeRecord(
    getLaneSmokeProbeLabel("vad"),
    "passed",
    `Energy VAD detected fixture speech across ${speech.voicedWindows} voiced frames; ` +
      `synthetic silence stayed below threshold (peak ${silenceDiagnostics.peakDbfs.toFixed(1)} dBFS).`,
    {
      peak_probability: speech.peakProbability,
      mean_probability: speech.averageProbability,
      voiced_windows: speech.voicedWindows,
      silence_peak: Number(silenceDiagnostics.peakDbfs.toFixed(2)),
    },
  );
}

async function runSttSmokeCheck(
  manifest: LaneManifest,
  onTelemetry?: (telemetry: { event: string; level?: "debug" | "info" | "warning" | "error"; msg?: string; data?: Record<string, unknown> }) => void,
): Promise<SmokeCheckRecord> {
  // whisper.cpp WASM smoke. Replaces the Moonshine + ORT smoke that bench
  // 05/06/08 measured as un-shippable on iPhone Safari (`InferenceSession.create()`
  // triggered iOS jetsam reproducibly). Bench 09 validated whisper.cpp WASM
  // end-to-end on iPhone, including airplane-mode reload + live mic
  // transcription. The smoke runs the same fixture through the same worker
  // that the live runtime path will use, so a passing smoke means the
  // live STT path has all of: model in OPFS, model loadable into FS,
  // whisper instance constructed, full_default returns success, and the
  // canonical end-marker arrives — same set of failure modes the live
  // path can surface.
  const fixture = await loadFixtureAudio();
  const result = await smokeWhisperTranscribe(manifest, fixture.mono16, { onTelemetry });
  return makeSmokeRecord(
    getLaneSmokeProbeLabel("stt"),
    "passed",
    `whisper.cpp transcribed ${fixture.mono16.length} PCM samples (${(fixture.mono16.length / 16000).toFixed(2)} s) into "${result.text.slice(0, 80)}${result.text.length > 80 ? "…" : ""}" in ${result.durationMs} ms (${result.segmentCount} segments, finish_reason=${result.finishReason ?? "?"}).`,
    {
      transcript_chars: result.text.length,
      segment_count: result.segmentCount,
      duration_ms: result.durationMs,
      finish_reason: result.finishReason ?? "unknown",
      stdout_lines: result.stdoutLines,
      stderr_lines: result.stderrLines,
      input_samples: fixture.mono16.length,
    },
  );
}

async function runTtsSmokeCheck(manifest: LaneManifest): Promise<SmokeCheckRecord> {
  const ort = getOnnxRuntime();
  const fixture = await loadFixtureAudio();

  if (fixture.sampleRate !== 48000) {
    throw new Error(`Fixture must stay at 48 kHz for TTS codec checks, got ${fixture.sampleRate}`);
  }

  const modelBytes = await readOpfsBytes(
    findManifestFilePath(manifest, "moss_audio_tokenizer_encode.onnx"),
  );
  const externalDataBytes = await readOpfsBytes(
    findManifestFilePath(manifest, "moss_audio_tokenizer_encode.data"),
  );
  const session = await ort.InferenceSession.create(modelBytes, {
    executionProviders: ["wasm"],
    externalData: [
      {
        path: "moss_audio_tokenizer_encode.data",
        data: externalDataBytes,
      },
    ],
  });

  try {
    const stereo = duplicateMonoToStereo(fixture.mono48);
    const result = await session.run({
      waveform: new ort.Tensor("float32", stereo, [1, 2, fixture.mono48.length]),
      input_lengths: new ort.Tensor("int32", new Int32Array([fixture.mono48.length]), [1]),
    });
    const codeLengths = intTensorData(result.audio_code_lengths);
    const dims = tensorDims(result.audio_codes);
    const codeLength = codeLengths[0] ?? 0;
    const codebooks = dims[2] ?? 0;

    if (codeLength <= 0 || codebooks !== 16) {
      throw new Error(`TTS codec check failed: length=${codeLength} codebooks=${codebooks}`);
    }

    return makeSmokeRecord(
      getLaneSmokeProbeLabel("tts"),
      "passed",
      `Codec encoder produced ${codeLength} frames across ${codebooks} codebooks from the fixture audio.`,
      {
        code_frames: codeLength,
        codebooks,
      },
    );
  } finally {
    await releaseSession(session);
  }
}

async function runLlmSmokeCheck(manifest: LaneManifest): Promise<SmokeCheckRecord> {
  const result = await runQwenPromptCheck(manifest);

  if (!result.responseValid) {
    throw new Error(
      `LLM returned "${result.outputText || "(empty)"}" for ${result.promptLabel}.`,
    );
  }

  return makeSmokeRecord(
    getLaneSmokeProbeLabel("llm"),
    "passed",
    `LLM answered "${result.outputText}" for ${result.promptLabel} in ${result.durationMs} ms.`,
    {
      duration_ms: result.durationMs,
      response_text: result.outputText,
      normalized_digits: result.normalizedDigits,
      response_valid: result.responseValid,
      n_ctx: result.context.n_ctx,
      n_batch: result.context.n_batch,
      n_ctx_train: result.context.n_ctx_train,
      threads: result.threads,
      multithread: result.multithread,
      model_architecture: result.context.metadata["general.architecture"] ?? "unknown",
    },
  );
}

export function pendingSmokeRecord(lane: Lane): SmokeCheckRecord {
  return makeSmokeRecord(
    getLaneSmokeProbeLabel(lane),
    "pending",
    `Downloaded into OPFS. Run the ${getLaneSmokeProbeLabel(lane)} device check next.`,
  );
}

export async function runLaneSmokeCheck(
  manifest: LaneManifest,
  bus: RuntimeBus,
  onTelemetry?: (telemetry: { event: string; level?: "debug" | "info" | "warning" | "error"; msg?: string; data?: Record<string, unknown> }) => void,
): Promise<SmokeCheckRecord> {
  bus.publish({
    type: "smoke.start",
    lane: manifest.lane,
    probe: getLaneSmokeProbeLabel(manifest.lane),
  });

  try {
    const record =
      manifest.lane === "vad"
        ? await runVadSmokeCheck(manifest)
        : manifest.lane === "stt"
          ? await runSttSmokeCheck(manifest, onTelemetry)
          : manifest.lane === "tts"
            ? await runTtsSmokeCheck(manifest)
            : await runLlmSmokeCheck(manifest);

    bus.publish({
      type: "smoke.ok",
      lane: manifest.lane,
      summary: record.summary,
    });

    return record;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Lane check failed";
    bus.publish({
      type: "smoke.fail",
      lane: manifest.lane,
      message,
    });
    throw error;
  }
}

export function mergeSmokeRecord(
  record: InstallRecord,
  smoke: SmokeCheckRecord,
): InstallRecord {
  return {
    ...record,
    smoke,
  };
}
